import { useState, useEffect } from 'react';
import { collection, query, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useTranslation } from '../../hooks/useTranslation';
import * as Icons from 'lucide-react';
import Select from 'react-select';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

export default function ReferralManager() {
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReferrers, setSelectedReferrers] = useState({});
  const [updating, setUpdating] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25); // 5x5 grid
  const [showNoReferrer, setShowNoReferrer] = useState(false);
  const [dateFilter, setDateFilter] = useState('');  // 'week', 'month', '3months', 'all'
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      console.log('Fetching customers...');
      
      const customersRef = collection(db, 'customers');
      const snapshot = await getDocs(customersRef);
      
      console.log('Fetched documents count:', snapshot.docs.length);
      
      if (snapshot.empty) {
        console.log('No customers found in the database');
        setCustomers([]);
        return;
      }
      
      const customersData = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('Customer data:', doc.id, data);
        return {
          docId: doc.id,
          id: data.id || doc.id, // Biztosítjuk, hogy mindig legyen id
          ...data
        };
      });
      
      console.log('Processed customers data:', customersData);
      setCustomers(customersData);
    } catch (error) {
      console.error('Error fetching customers:', error);
      setError(error.message);
      toast.error('Hiba történt az ügyfelek betöltésekor: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReferral = async (customerId, newReferrerId) => {
    try {
      console.log('Updating referral:', { customerId, newReferrerId });
      
      if (newReferrerId === undefined) {
        toast.error('Kérjük, válasszon egy érvényes ajánlót vagy válassza a "Nincs ajánló" opciót');
        return;
      }
      
      setUpdating(prev => ({ ...prev, [customerId]: true }));
      
      const customer = customers.find(c => c.docId === customerId || c.id === customerId);
      if (!customer) {
        throw new Error(`Customer not found with ID: ${customerId}`);
      }
      
      console.log('Found customer to update:', customer);
      
      let newReferrer = null;
      if (newReferrerId !== null) {
        newReferrer = customers.find(c => c.docId === newReferrerId || c.id === newReferrerId);
        if (!newReferrer) {
          throw new Error(`Referrer not found with ID: ${newReferrerId}`);
        }
        console.log('Found new referrer:', newReferrer);
      }
      
      // Ellenőrizzük, hogy a dokumentum ID létezik-e
      if (!customer.docId) {
        throw new Error('Customer document ID is missing');
      }
      
      console.log('Updating Firestore document:', customer.docId);
      
      const customerRef = doc(db, 'customers', customer.docId);
      await updateDoc(customerRef, {
        ref_id: newReferrer?.docId || null,
        updatedAt: new Date()
      });
      
      console.log('Firestore update successful');
      
      toast.success(
        newReferrerId
          ? t('referralManager.updateSuccess', {
              username: customer.username || customer.name || customer.id,
              referrer: newReferrer.username || newReferrer.name || newReferrer.id
            })
          : t('referralManager.removeSuccess', {
              username: customer.username || customer.name || customer.id
            })
      );
      
      // Frissítjük a listát
      await fetchCustomers();
    } catch (error) {
      console.error('Error updating referral:', error);
      toast.error(`${t('referralManager.updateError')}: ${error.message}`);
    } finally {
      setUpdating(prev => ({ ...prev, [customerId]: false }));
    }
  };

  const filteredCustomers = customers.filter(customer => {
    console.log('Filtering customer:', customer.username || customer.name || customer.id);
    
    const now = new Date();
    const customerDate = customer.created_at?.toDate ? customer.created_at?.toDate() : customer.createdAt?.toDate ? customer.createdAt?.toDate() : null;
    
    const isWithinDate = () => {
      if (!dateFilter || !customerDate) return true;
      const diff = now - customerDate;
      const days = diff / (1000 * 60 * 60 * 24);
      switch (dateFilter) {
        case 'week': return days <= 7;
        case 'month': return days <= 30;
        case '3months': return days <= 90;
        default: return true;
      }
    };
    
    // Keresési feltételek ellenőrzése
    const matchesSearch = !searchTerm || 
      (customer.username && customer.username.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.email && customer.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.name && customer.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.id && customer.id.toString().includes(searchTerm));
    
    // Ajánló nélküli szűrés
    const matchesReferrerFilter = !showNoReferrer || !customer.ref_id;
    
    // Dátum szűrés
    const matchesDateFilter = isWithinDate();
    
    const shouldInclude = matchesSearch && matchesReferrerFilter && matchesDateFilter;
    console.log(`Customer ${customer.username || customer.name || customer.id} included: ${shouldInclude}`);
    
    return shouldInclude;
  });

  console.log('Filtered customers count:', filteredCustomers.length);

  // Megkeressük egy ügyfél referrerét
  const findReferrer = (refId) => {
    if (!refId) return null;
    
    console.log('Finding referrer for refId:', refId);
    
    // Próbáljuk megtalálni a referrert különböző ID formátumok alapján
    const referrer = customers.find(c => 
      c.docId === refId || 
      c.id === refId || 
      (typeof c.docId === 'string' && typeof refId === 'string' && c.docId === refId) ||
      (typeof c.id === 'string' && typeof refId === 'string' && c.id === refId) ||
      (typeof c.id === 'number' && typeof refId === 'number' && c.id === refId) ||
      (typeof c.id === 'number' && typeof refId === 'string' && c.id.toString() === refId) ||
      (typeof c.id === 'string' && typeof refId === 'number' && c.id === refId.toString())
    );
    
    console.log('Found referrer:', referrer?.username || referrer?.name || referrer?.id || 'Not found');
    
    return referrer;
  };

  // Számoljuk ki a megjelenítendő elemeket
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCustomers = filteredCustomers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Select options formázása
  const getReferrerOptions = (currentCustomerId) => {
    return [
      { value: null, label: t('referralManager.removeReferrer') },
      ...customers
        .filter(c => c.docId !== currentCustomerId && c.id !== currentCustomerId)
        .map(c => ({
          value: c.docId || c.id,
          label: `${c.username || c.name || c.email || c.id || 'Ismeretlen felhasználó'} (ID: ${c.docId || c.id})`
        }))
    ];
  };

  // Ajánló opció formázása a Select komponenshez
  const renderReferralOption = ({ value, label }) => {
    if (value === null) {
      return (
        <div className="flex items-center gap-2 text-red-600">
          <Icons.X className="h-4 w-4" />
          <span>{t('referralManager.removeReferrer')}</span>
        </div>
      );
    }
    
    const customer = customers.find(
      c => c.docId === value || c.id === value
    );
    
    if (!customer) return label;

    return (
      <div className="flex items-center gap-2">
        <div className="flex-shrink-0">
          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
            {(customer.username || customer.name || customer.email || '?').charAt(0).toUpperCase()}
          </div>
        </div>
        <div>
          <div className="text-sm font-medium text-gray-900">
            {customer.username || customer.name || customer.email || 'Ismeretlen felhasználó'}
          </div>
          <div className="text-xs text-gray-500">
            ID: {customer.docId || customer.id}
          </div>
        </div>
      </div>
    );
  };

  // Custom styles a Select komponenshez
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '32px',
      height: '32px',
      backgroundColor: 'white',
      borderColor: '#D1D5DB',
      '&:hover': {
        borderColor: '#9CA3AF'
      }
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0 8px',
      height: '32px'
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '0.875rem',
      padding: '4px 8px',
      backgroundColor: state.isFocused ? '#F3F4F6' : 'white',
      color: state.isFocused ? '#111827' : '#374151',
      '&:active': {
        backgroundColor: '#E5E7EB'
      }
    })
  };

  // Új függvények a statisztikákhoz
  const calculateStats = () => {
    const stats = {
      totalCustomers: customers.length,
      withReferrer: customers.filter(c => c.ref_id).length,
      topReferrers: [],
      averageTeamSize: 0,
      deepestChain: 0
    };

    // Számoljuk meg minden felhasználóhoz a referáltakat
    const referralCounts = customers.reduce((acc, customer) => {
      const referredCount = customers.filter(c => c.ref_id === customer.id).length;
      if (referredCount > 0) {
        acc.push({
          ...customer,
          referredCount
        });
      }
      return acc;
    }, []);

    // Top 5 legtöbb referrallal rendelkező felhasználó
    stats.topReferrers = referralCounts
      .sort((a, b) => b.referredCount - a.referredCount)
      .slice(0, 5);

    // Átlagos csapat méret
    if (referralCounts.length > 0) {
      stats.averageTeamSize = (
        referralCounts.reduce((sum, item) => sum + item.referredCount, 0) / 
        referralCounts.length
      ).toFixed(1);
    }

    // Leghosszabb lánc meghatározása
    const getChainLength = (userId, visited = new Set()) => {
      if (visited.has(userId)) return 0;
      visited.add(userId);
      
      const referrals = customers.filter(c => c.ref_id === userId);
      if (referrals.length === 0) return 1;

      return 1 + Math.max(...referrals.map(r => getChainLength(r.id, new Set(visited))));
    };

    stats.deepestChain = Math.max(
      ...customers.map(c => getChainLength(c.id))
    );

    return stats;
  };

  const stats = calculateStats();

  const buildReferralTree = (customers) => {
    const customerMap = new Map();
    customers.forEach(customer => {
      customerMap.set(customer.docId, { ...customer, children: [] });
    });

    const tree = [];
    customers.forEach(customer => {
      if (customer.ref_id) {
        const parent = customerMap.get(customer.ref_id) || 
          Array.from(customerMap.values()).find(c => c.id === customer.ref_id);
        if (parent) {
          parent.children.push(customerMap.get(customer.docId));
        } else {
          tree.push(customerMap.get(customer.docId));
        }
      } else {
        tree.push(customerMap.get(customer.docId));
      }
    });

    return tree;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Icons.Loader className="animate-spin h-8 w-8 text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('referralManager.title', 'Referral Manager')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('referralManager.subtitle', 'Manage customer referral relationships')}</p>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          <div className="flex items-center">
            <Icons.AlertTriangle className="h-5 w-5 mr-2 text-red-500" />
            <span className="font-medium">Hiba történt az adatok betöltésekor</span>
          </div>
          <p className="mt-2 text-sm">{error}</p>
          <button 
            onClick={fetchCustomers}
            className="mt-3 inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
          >
            <Icons.RefreshCw className="h-4 w-4 mr-2" />
            Újrapróbálkozás
          </button>
        </div>
      </div>
    );
  }

  if (customers.length === 0) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('referralManager.title', 'Referral Manager')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('referralManager.subtitle', 'Manage customer referral relationships')}</p>
        </div>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <Icons.Users className="h-12 w-12 mx-auto text-yellow-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nincsenek ügyfelek</h3>
          <p className="text-gray-600 mb-4">
            Nem találhatók ügyfelek az adatbázisban. Kérjük, hozzon létre új ügyfeleket, vagy ellenőrizze az adatbázis kapcsolatot.
          </p>
          <button 
            onClick={fetchCustomers}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <Icons.RefreshCw className="h-4 w-4 mr-2" />
            Frissítés
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('referralManager.title', 'Referral Manager')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('referralManager.subtitle', 'Manage customer referral relationships')}</p>
      </div>

      {/* Statisztikai panel */}
      <div className="mb-6 grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Kereső és statisztika */}
        <div className="col-span-2 grid grid-cols-2 gap-4">
          {/* Kereső kártya */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm text-gray-500">{t('common.search')}</div>
              <div className="h-8 w-8 bg-gray-50 rounded-lg flex items-center justify-center">
                <Icons.Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            <div className="space-y-2">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('referralManager.searchPlaceholder')}
                className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setShowNoReferrer(!showNoReferrer)}
                  className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-colors ${
                    showNoReferrer 
                    ? 'bg-red-100 text-red-700 hover:bg-red-200' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {showNoReferrer ? (
                    <Icons.X className="h-3 w-3" />
                  ) : (
                    <Icons.Filter className="h-3 w-3" />
                  )}
                  {t('referralManager.noReferrerFilter')}
                </button>

                <div className="flex items-center gap-1">
                  {[
                    { value: 'week', label: t('referralManager.lastWeek') },
                    { value: 'month', label: t('referralManager.lastMonth') },
                    { value: '3months', label: t('referralManager.last3Months') },
                    { value: '', label: t('referralManager.allTime') }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => setDateFilter(option.value)}
                      className={`text-xs px-2 py-1 rounded-md transition-colors ${
                        dateFilter === option.value
                        ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {t('common.showing')} {filteredCustomers.length} {t('common.of')} {customers.length}
            </div>
          </div>

          {/* Statisztika kártya */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-500">{t('referralManager.averageTeamSize')}</div>
                <div className="text-xl font-semibold mt-1">{stats.averageTeamSize}</div>
              </div>
              <div className="h-8 w-8 bg-green-50 rounded-lg flex items-center justify-center">
                <Icons.Users className="h-5 w-5 text-green-600" />
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">
              {t('referralManager.deepestChain')}: {stats.deepestChain}
            </div>
          </div>
        </div>

        {/* Top ajánlók */}
        <div className="col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm text-gray-500">{t('referralManager.topReferrers')}</div>
            <div className="h-8 w-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Icons.Trophy className="h-5 w-5 text-blue-600" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {stats.topReferrers.slice(0, 6).map((referrer, index) => (
              <div 
                key={referrer.id} 
                className={`flex items-center gap-2 text-sm ${
                  index === 0 || index === 1 ? 'bg-indigo-50' : 'bg-gray-50'
                } rounded-md p-2`}
              >
                <div className={`w-4 font-semibold ${
                  index === 0 ? 'text-yellow-500' :
                  index === 1 ? 'text-gray-400' :
                  'text-gray-400'
                }`}>
                  #{index + 1}
                </div>
                <div className="h-6 w-6 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center text-xs font-medium text-gray-600">
                  {referrer.username?.charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-gray-900 truncate">
                    {referrer.username}
                  </div>
                  <div className="text-xs text-gray-500 truncate">
                    {referrer.email || '-'}
                  </div>
                </div>
                <div className="text-xs font-semibold text-indigo-600 whitespace-nowrap">
                  {referrer.referredCount} {t('referralManager.members')}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Grid módosítása */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {currentCustomers.length === 0 ? (
          <div className="col-span-full bg-gray-50 rounded-lg p-8 text-center">
            <Icons.Search className="h-10 w-10 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Nincs találat</h3>
            <p className="text-gray-600">
              A keresési feltételeknek megfelelő ügyfelek nem találhatók. Próbálja meg módosítani a keresési feltételeket.
            </p>
          </div>
        ) : (
          currentCustomers.map((customer) => {
            const referrer = findReferrer(customer.ref_id);
            const displayName = customer.username || customer.name || customer.id;
            const displayEmail = customer.email || '-';
            const displayId = customer.id || customer.docId || '-';
            
            return (
              <div 
                key={customer.docId || customer.id}
                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
              >
                <div className="flex flex-col gap-3">
                  {/* Felhasználó fejléc */}
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-sm font-medium text-white">
                      {(displayName?.charAt(0) || '?').toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <Link 
                        to={`/admin/customers/${displayId}`}
                        className="group flex items-center gap-1 text-base font-semibold text-indigo-700 hover:text-indigo-800"
                      >
                        <span className="truncate">{displayName}</span>
                        <Icons.ExternalLink className="h-3.5 w-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                      <div className="text-xs font-medium text-gray-500">
                        ID: {displayId}
                      </div>
                    </div>
                  </div>

                  {/* Felhasználó adatok */}
                  <div className="text-sm space-y-1">
                    <div className="text-gray-900 font-medium truncate bg-gray-50 px-2 py-1 rounded">
                      {customer.full_name || customer.name || '-'}
                    </div>
                    <div className="text-indigo-600 truncate text-xs font-medium hover:text-indigo-700">
                      {displayEmail}
                    </div>
                  </div>

                  {/* Jelenlegi ajánló */}
                  <div className="text-sm space-y-1">
                    <div className="text-gray-500 mb-1 font-medium">
                      {t('referralManager.currentReferrer')}:
                    </div>
                    {referrer ? (
                      <div className="bg-gradient-to-r from-gray-50 to-indigo-50 p-2 rounded-md border border-indigo-100">
                        <div className="flex items-center gap-2">
                          <div className="h-6 w-6 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-xs font-medium text-white">
                            {(referrer.username?.charAt(0) || referrer.name?.charAt(0) || '?').toUpperCase()}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="font-medium text-indigo-700 truncate">
                              {referrer.username || referrer.name || referrer.id}
                            </div>
                            <div className="text-xs text-indigo-600 truncate">
                              {referrer.email || '-'}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-red-50 p-2 rounded-md text-red-600 border border-red-100 italic font-medium">
                        <div className="flex items-center gap-2">
                          <Icons.AlertCircle className="h-4 w-4 text-red-500" />
                          {t('referralManager.noReferrer')}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Ajánló választó és mentés gomb */}
                  <div className="space-y-2">
                    <Select
                      value={
                        selectedReferrers[customer.docId || customer.id] 
                          ? {
                              value: selectedReferrers[customer.docId || customer.id],
                              label: (() => {
                                const selectedRef = customers.find(c => 
                                  c.docId === selectedReferrers[customer.docId || customer.id] || 
                                  c.id === selectedReferrers[customer.docId || customer.id]
                                );
                                return selectedRef 
                                  ? `${selectedRef.username || selectedRef.name || selectedRef.email || selectedRef.id || 'Ismeretlen felhasználó'} (ID: ${selectedRef.docId || selectedRef.id})`
                                  : `ID: ${selectedReferrers[customer.docId || customer.id]}`;
                              })()
                            }
                          : null
                      }
                      onChange={(option) => {
                        setSelectedReferrers(prev => ({
                          ...prev,
                          [customer.docId || customer.id]: option?.value
                        }));
                      }}
                      options={getReferrerOptions(customer.id || customer.docId)}
                      isSearchable={true}
                      isClearable={true}
                      placeholder={t('referralManager.selectReferrer')}
                      noOptionsMessage={() => t('referralManager.noOptions')}
                      styles={selectStyles}
                      className="text-sm"
                      menuPlacement="auto"
                      maxMenuHeight={200}
                      formatOptionLabel={renderReferralOption}
                    />
                    <button
                      onClick={() => handleUpdateReferral(customer.docId || customer.id, selectedReferrers[customer.docId || customer.id])}
                      disabled={updating[customer.docId || customer.id]}
                      className="w-full inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {updating[customer.docId || customer.id] ? (
                        <Icons.Loader className="animate-spin h-4 w-4" />
                      ) : (
                        t('common.save')
                      )}
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 flex justify-between items-center bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <button
            onClick={() => paginate(currentPage - 1)}
            disabled={currentPage === 1}
            className={`px-3 py-1 rounded ${
              currentPage === 1 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icons.ChevronLeft className="h-5 w-5" />
          </button>

          <div className="flex space-x-1">
            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              // Show first page, last page, current page, and pages around current
              if (
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2)
              ) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={`px-3 py-1 rounded ${
                      currentPage === pageNumber
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              } else if (
                pageNumber === currentPage - 3 ||
                pageNumber === currentPage + 3
              ) {
                return <span key={pageNumber} className="px-2 text-gray-400">...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === totalPages}
            className={`px-3 py-1 rounded ${
              currentPage === totalPages 
                ? 'text-gray-400 cursor-not-allowed' 
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <Icons.ChevronRight className="h-5 w-5" />
          </button>

          <div className="text-sm text-gray-500">
            {t('common.showing', 'Showing')} {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredCustomers.length)} {t('common.of', 'of')} {filteredCustomers.length}
          </div>
        </div>
      )}
    </div>
  );
} 